import { useCallback, useEffect, useMemo, useState } from 'react';
import { ResearchReportsResponse, ReportItem, SortMode, ResearchReport } from '../types';
import { useAsync } from 'react-use';
import { useDashboardContext } from '@shared/contexts/DashboardContext';
import { useApi } from '@hooks/useApi';

export const useReports = (widgetId: string, selectedReport?: ReportItem, savedSortMode?: SortMode) => {
  const [availableReports, setAvailableReports] = useState<ResearchReportsResponse>();
  const [currentReport, setCurrentReport] = useState<string>('');
  const [currentSelected, setCurrentSelected] = useState<ReportItem | undefined>(selectedReport);
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const { editWidgetPayloadById } = useDashboardContext();
  const [sortMode, setSortMode] = useState<SortMode>(savedSortMode || SortMode.NEWEST);
  const getReportType = (report: ResearchReport) => {
    if (report.category.startsWith('insight')) return 1;
    if (report.category.startsWith('cot')) return 2;
    if (report.category.startsWith('cftc')) return 3;
    return 4;
  };
  const sortedReports = useMemo(() => {
    if (!availableReports) return undefined;
    return [...availableReports].sort((a, b) => {
      switch (sortMode) {
        case SortMode.NEWEST: {
          const timeCompareNew = new Date(b.report[0].latest_publish_date).getTime() - new Date(a.report[0].latest_publish_date).getTime();
          return timeCompareNew !== 0 ? timeCompareNew : a.category.localeCompare(b.category);
        }
        case SortMode.OLDEST: {
          const timeCompareOld = new Date(a.report[0].latest_publish_date).getTime() - new Date(b.report[0].latest_publish_date).getTime();
          return timeCompareOld !== 0 ? timeCompareOld : a.category.localeCompare(b.category);
        }
        case SortMode.CATEGORY: {
          const diff = getReportType(a) - getReportType(b);
          if (diff !== 0) return diff;
          const categoryDiff = a.category.localeCompare(b.category);
          if (categoryDiff !== 0) return categoryDiff;
          return new Date(b.report[0].latest_publish_date).getTime() - new Date(a.report[0].latest_publish_date).getTime();
        }
      }
    });
  }, [availableReports, sortMode]);

  const { apiClient } = useApi();

  useEffect(() => {
    if (selectedReport) {
      setCurrentSelected(selectedReport);
      setCurrentReport(selectedReport.report_link);

      if (availableReports) {
        const parentCategory = availableReports.find(
          category =>
            category.category === selectedReport.category &&
            category.report.some(report => report.report_name === selectedReport.report_name && report.report_link === selectedReport.report_link)
        );

        if (parentCategory) {
          setOpen(prev => ({ ...prev, [parentCategory.category]: true }));
        }
      }
    }
  }, [selectedReport, availableReports]);

  useAsync(async () => {
    const reports = await apiClient?.getResearchReports();
    if (reports) {
      setAvailableReports(reports);
    }
  }, [apiClient]);

  const onReportClick = (report: ReportItem) => {
    const isSameReportLink = report.report_link === currentReport;
    const isSameCategory = report.category === currentSelected?.category;

    setCurrentSelected(report);

    if (isSameReportLink && isSameCategory) return;
    editWidgetPayloadById(widgetId, { selectedReport: report, sortMode: sortMode });

    if (!isSameReportLink) {
      setIsLoading(true);
      setCurrentReport(report.report_link);
    }
  };

  const onParentReportClick = useCallback(
    (report: string) => {
      if (open[report]) {
        setOpen({ ...open, [report]: false });
      } else {
        setOpen({ ...open, [report]: true });
      }
    },
    [open]
  );

  const updateSortMode = (sortMode: SortMode) => {
    setSortMode(sortMode);
    editWidgetPayloadById(widgetId, { sortMode: sortMode, selectedReport: currentSelected });
  };

  const onToggleReportsList = () => {
    setIsVisible(!isVisible);
  };

  const onIframeLoad = () => {
    setIsLoading(false);
  };

  return {
    availableReports,
    currentReport,
    currentSelected,
    isVisible,
    isLoading,
    onReportClick,
    onToggleReportsList,
    onIframeLoad,
    onParentReportClick,
    open,
    updateSortMode,
    sortMode,
    sortedReports,
  };
};
